import { createModel } from "@rematch/core";
import * as _ from "lodash";
import { CustomerGroupListItem } from "src/lib/models/CustomerGroupListItem";
import {
  CustomerGroupFilterState,
  CustomerGroupListState,
  CustomerGroups,
  ICustomerGroupExport,
} from "src/types";
import * as xlsx from "xlsx";

import * as api from "../api";
import { RootModel } from ".";
import { checkToken, signoutIfUnauthorized } from "./utilities";
import { adaptSavedFilterSettings } from "./utilities";

const initialState: CustomerGroupListState = {
  payload: {},
  meta: { pending: false },
  error: false,
};

function normalize(customerGroupList: CustomerGroupListItem[]) {
  const normalizedCustomerGroupList: CustomerGroups = {};
  Object.values(customerGroupList).forEach((cg) => {
    normalizedCustomerGroupList[cg.uuid] = {
      ...cg,
    };
  });
  return normalizedCustomerGroupList;
}

const changePending = (state: CustomerGroupListState) => ({
  ...state,
  meta: { ...state.meta, pending: true },
  error: false,
});

const changeFail = (state: CustomerGroupListState) => ({
  ...state,
  meta: { ...state.meta, pending: false },
  error: true,
});

export const customerGroupList = createModel<RootModel>()({
  state: initialState as CustomerGroupListState,
  reducers: {
    fetchCustomerGroupListPending() {
      return {
        ...initialState,
        meta: { ...initialState.meta, pending: true },
        error: false,
      };
    },
    saveCustomerGroupPending(state) {
      return changePending(state);
    },
    editCustomerGroupPending(state) {
      return changePending(state);
    },
    deleteCustomerGroupPending(state) {
      return changePending(state);
    },
    saveCustomerGroupExportPending(state) {
      return changePending(state);
    },
    deleteCustomerGroupExportPending(state) {
      return changePending(state);
    },
    fetchCustomerGroupListFail() {
      return {
        ...initialState,
        meta: { ...initialState.meta, pending: false },
        error: true,
      };
    },
    saveCustomerGroupFail(state) {
      return changeFail(state);
    },
    editCustomerGroupFail(state) {
      return changeFail(state);
    },
    deleteCustomerGroupFail(state) {
      return changeFail(state);
    },
    saveCustomerGroupExportFail(state) {
      return changeFail(state);
    },
    deleteCustomerGroupExportFail(state) {
      return changeFail(state);
    },
    downloadCustomerGroupExportFail(state) {
      return changeFail(state);
    },
    fetchCustomerGroupListSuccess(state, payload: CustomerGroupListItem[]) {
      return {
        ...state,
        payload: normalize(payload),
        meta: { ...state.meta, pending: false },
        error: false,
      };
    },
    saveCustomerGroupSuccess(state, payload: CustomerGroupListItem) {
      const customerGroup = payload;
      return {
        ...state,
        payload: {
          ...state.payload,
          [customerGroup.uuid]: customerGroup,
        },
        meta: { ...state.meta, pending: false },
        error: false,
      };
    },
    editCustomerGroupSuccess(state, payload: CustomerGroupListItem) {
      const customerGroup = payload;
      return {
        ...state,
        payload: {
          ...state.payload,
          [customerGroup.uuid]: customerGroup,
        },
        meta: { ...state.meta, pending: false },
        error: false,
      };
    },
    deleteCustomerGroupSuccess(state, payload: { uuid: string }) {
      const { uuid } = payload;
      const customerGroups = { ...state.payload };
      delete customerGroups[uuid];
      return {
        ...state,
        meta: { ...state.meta, pending: false },
        payload: customerGroups,
        error: false,
      };
    },
    saveCustomerGroupExportSuccess(state, payload: ICustomerGroupExport) {
      const customerGroupExport = payload;
      const { groupUuid } = customerGroupExport;
      const customerGroup = state.payload[groupUuid];
      const customerGroupExports = [
        customerGroupExport,
        ...customerGroup.groupExports,
      ];
      return {
        ...state,
        meta: { ...state.meta, pending: false },
        payload: {
          ...state.payload,
          [groupUuid]: { ...customerGroup, groupExports: customerGroupExports },
        },
        error: false,
      };
    },
    deleteCustomerGroupExportSuccess(state, payload: ICustomerGroupExport) {
      const { groupUuid, uuid } = payload;
      const customerGroup = state.payload[groupUuid];
      const customerGroupExports = _.reject(
        customerGroup.groupExports,
        (item) => item.uuid === uuid
      );
      return {
        ...state,
        meta: { ...state.meta, pending: false },
        payload: {
          ...state.payload,
          [groupUuid]: { ...customerGroup, groupExports: customerGroupExports },
        },
        error: false,
      };
    },
    downloadCustomerGroupExportSuccess(state) {
      return {
        ...state,
        meta: { ...state.meta, pending: false },
        error: false,
      };
    },
  },
  effects: (dispatch) => ({
    async fetchCustomerGroupList(payload: null, rootState) {
      dispatch.customerGroupList.fetchCustomerGroupListPending();

      const token = checkToken(rootState, () =>
        dispatch.customerGroupList.fetchCustomerGroupListFail()
      );
      if (!token) return;

      try {
        const customerGroupListResult = await api.fetchCustomerGroupList(token);
        dispatch.customerGroupList.fetchCustomerGroupListSuccess(
          adaptSavedFilterSettings(customerGroupListResult)
        );
      } catch (error) {
        signoutIfUnauthorized(error, dispatch);
        dispatch.customerGroupList.fetchCustomerGroupListFail();
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    async editCustomerGroup(
      {
        filterSettings,
        groupName,
        customerGroupUuid,
      }: {
        filterSettings?: CustomerGroupFilterState;
        groupName?: string;
        customerGroupUuid: string;
      },
      rootState
    ) {
      dispatch.customerGroupList.editCustomerGroupPending();

      const token = checkToken(rootState, () =>
        dispatch.customerGroupList.editCustomerGroupFail()
      );
      if (!token) return;

      try {
        const editCustomerGroupResult = await api.editCustomerGroup({
          token,
          filterSettings,
          groupName,
          customerGroupUuid,
        });
        dispatch.customerGroupList.editCustomerGroupSuccess(
          editCustomerGroupResult
        );
      } catch (error) {
        signoutIfUnauthorized(error, dispatch);
        dispatch.customerGroupList.editCustomerGroupFail();
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    async saveCustomerGroup(groupName: string, rootState) {
      dispatch.customerGroupList.saveCustomerGroupPending();

      const token = checkToken(rootState, () =>
        dispatch.customerGroupList.saveCustomerGroupFail()
      );
      if (!token) return;

      try {
        const saveCustomerGroupResult = await api.saveCustomerGroup(
          token,
          rootState.customerGroupFilter,
          groupName
        );
        dispatch.customerGroupList.saveCustomerGroupSuccess(
          saveCustomerGroupResult
        );
      } catch (error) {
        signoutIfUnauthorized(error, dispatch);
        dispatch.customerGroupList.saveCustomerGroupFail();
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    async deleteCustomerGroup(uuid: string, rootState) {
      dispatch.customerGroupList.deleteCustomerGroupPending();

      const token = checkToken(rootState, () =>
        dispatch.customerGroupList.deleteCustomerGroupFail()
      );
      if (!token) return;

      try {
        const deleteCustomerGroupResult = await api.deleteCustomerGroup(
          token,
          uuid
        );
        dispatch.customerGroupList.deleteCustomerGroupSuccess(
          deleteCustomerGroupResult
        );
      } catch (error) {
        signoutIfUnauthorized(error, dispatch);
        dispatch.customerGroupList.deleteCustomerGroupFail();
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    async saveCustomerGroupExport(
      {
        customerGroupUuid,
        comment,
      }: { customerGroupUuid: string; comment?: string | null },
      rootState
    ) {
      dispatch.customerGroupList.saveCustomerGroupExportPending();

      const token = checkToken(rootState, () =>
        dispatch.customerGroupList.saveCustomerGroupExportFail()
      );
      if (!token) return;

      try {
        const saveCustomerGroupExportResult = await api.saveCustomerGroupExport(
          token,
          customerGroupUuid,
          comment
        );
        dispatch.customerGroupList.saveCustomerGroupExportSuccess(
          saveCustomerGroupExportResult
        );
      } catch (error) {
        signoutIfUnauthorized(error, dispatch);
        dispatch.customerGroupList.saveCustomerGroupExportFail();
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    async downloadCustomerGroupExport(
      {
        customerGroupUuid,
        customerGroupExportUuid,
      }: { customerGroupUuid: string; customerGroupExportUuid: string },
      rootState
    ) {
      const token = checkToken(rootState, () =>
        dispatch.customerGroupList.downloadCustomerGroupExportFail()
      );
      if (!token) return;

      try {
        const response = await api.downloadCustomerGroupExport(
          token,
          customerGroupUuid,
          customerGroupExportUuid
        );
        const data = xlsx.read(new Uint8Array(response), {
          type: "array",
        });
        const dateString = new Date()
          .toLocaleString()
          .replace(/\./g, "-")
          .replace(/:/g, "-")
          .replace(", ", "_");
        xlsx.writeFileXLSX(data, `Kundengruppenexport_${dateString}.xlsx`);
        dispatch.customerGroupList.downloadCustomerGroupExportSuccess();
      } catch (error) {
        signoutIfUnauthorized(error, dispatch);
        dispatch.customerGroupList.downloadCustomerGroupExportFail();
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    async deleteCustomerGroupExport(
      {
        customerGroupUuid,
        customerGroupExportUuid,
      }: { customerGroupUuid: string; customerGroupExportUuid: string },
      rootState
    ) {
      dispatch.customerGroupList.deleteCustomerGroupExportPending();

      const token = checkToken(rootState, () =>
        dispatch.customerGroupList.deleteCustomerGroupExportFail()
      );
      if (!token) return;

      try {
        const deleteCustomerGroupExportResult = await api.deleteCustomerGroupExport(
          token,
          customerGroupUuid,
          customerGroupExportUuid
        );
        dispatch.customerGroupList.deleteCustomerGroupExportSuccess(
          deleteCustomerGroupExportResult
        );
      } catch (error) {
        signoutIfUnauthorized(error, dispatch);
        dispatch.customerGroupList.deleteCustomerGroupExportFail();
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
  }),
});



// WEBPACK FOOTER //
// ./src/models/customerGroupList.ts